import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CreditCard,
  Loader2,
  Save,
  Palette,
  Coffee,
  Sparkles,
  Smartphone,
  CheckCircle2,
} from "lucide-react";

import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_auth/admin/loyalty")({
  component: LoyaltyManagementPage,
});

// Curated premium color palettes for easy selection
const PRESET_PALETTES = [
  { name: "Espresso Brown", bg: "#5c4033", fg: "#ffffff", label: "#dddddd" },
  { name: "Midnight Black", bg: "#121212", fg: "#ffffff", label: "#a0a0a0" },
  { name: "Forest Green", bg: "#1b4332", fg: "#ffffff", label: "#b7e4c7" },
  { name: "Royal Blue", bg: "#1d3557", fg: "#ffffff", label: "#a8dadc" },
  { name: "Deep Burgundy", bg: "#4a1525", fg: "#ffffff", label: "#e0aaff" },
];

function LoyaltyManagementPage() {
  // Query settings
  const {
    data: settings,
    isLoading,
    refetch,
  } = useQuery({
    ...orpc.loyalty.getSettings.queryOptions(),
  });

  // Mutate settings
  const updateSettingsMutation = useMutation({
    ...orpc.loyalty.updateSettings.mutationOptions({
      onSuccess: () => {
        toast.success("Sadakat kartı ayarları güncellendi, tüm cihazlar yenileniyor.");
        refetch();
      },
      onError: (err) => {
        toast.error(err.message || "Ayarlar güncellenirken bir hata oluştu.");
      },
    }),
  });

  // Local state for live preview customization
  const [cardTitle, setCardTitle] = useState("Sadakat Kartı");
  const [backgroundColor, setBackgroundColor] = useState("#5c4033");
  const [foregroundColor, setForegroundColor] = useState("#ffffff");
  const [labelColor, setLabelColor] = useState("#dddddd");
  const [targetStamps, setTargetStamps] = useState(10);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // Sync state with fetched settings
  useEffect(() => {
    if (settings) {
      setCardTitle(settings.cardTitle);
      setBackgroundColor(settings.backgroundColor);
      setForegroundColor(settings.foregroundColor);
      setLabelColor(settings.labelColor);
      setTargetStamps(settings.targetStamps);
      setLogoUrl(settings.logoUrl);
    }
  }, [settings]);

  const handleApplyPalette = (palette: (typeof PRESET_PALETTES)[0]) => {
    setBackgroundColor(palette.bg);
    setForegroundColor(palette.fg);
    setLabelColor(palette.label);
    toast.info(`${palette.name} renk paleti uygulandı.`);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettingsMutation.mutate({
      cardTitle,
      backgroundColor,
      foregroundColor,
      labelColor,
      targetStamps,
      logoUrl,
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
      {/* Title Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-gray-900 flex items-center gap-2">
            <CreditCard className="size-6 text-blue-600" />
            Sadakat Kartı (Wallet) Ayarları
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Müşterilerinizin Apple & Google Wallet uygulamalarına ekleyebileceği dijital damga
            kartlarını tasarlayın ve yönetin.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left Side: Designer Form */}
        <form onSubmit={handleSave} className="lg:col-span-7 space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-6">
            <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
              <Palette className="size-5 text-gray-500" />
              <h2 className="text-base font-bold text-gray-800">Kart Görsel Tasarımı</h2>
            </div>

            {/* Title & Stamps Config */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">
                  Kart Başlığı
                </label>
                <input
                  type="text"
                  value={cardTitle}
                  onChange={(e) => setCardTitle(e.target.value)}
                  className="mt-1.5 block w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none shadow-sm"
                  placeholder="Sadakat Kartı"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">
                  Hedef Damga Sayısı
                </label>
                <select
                  value={targetStamps}
                  onChange={(e) => setTargetStamps(Number(e.target.value))}
                  className="mt-1.5 block w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none shadow-sm font-semibold"
                >
                  <option value={8}>8 Damga (Hızlı Ödül)</option>
                  <option value={10}>10 Damga (Standart)</option>
                  <option value={12}>12 Damga (Zor Ödül)</option>
                </select>
              </div>
            </div>

            {/* Colors picker */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">
                  Kart Rengi (Arka Plan)
                </label>
                <div className="mt-1.5 flex items-center gap-2">
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="size-10 rounded-xl border border-gray-200 p-0.5 cursor-pointer shadow-sm"
                  />
                  <input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="block w-full rounded-xl border border-gray-200 px-3 py-2 text-xs  uppercase focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">
                  Birincil Yazı Rengi
                </label>
                <div className="mt-1.5 flex items-center gap-2">
                  <input
                    type="color"
                    value={foregroundColor}
                    onChange={(e) => setForegroundColor(e.target.value)}
                    className="size-10 rounded-xl border border-gray-200 p-0.5 cursor-pointer shadow-sm"
                  />
                  <input
                    type="text"
                    value={foregroundColor}
                    onChange={(e) => setForegroundColor(e.target.value)}
                    className="block w-full rounded-xl border border-gray-200 px-3 py-2 text-xs  uppercase focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">
                  Etiket/Açıklama Rengi
                </label>
                <div className="mt-1.5 flex items-center gap-2">
                  <input
                    type="color"
                    value={labelColor}
                    onChange={(e) => setLabelColor(e.target.value)}
                    className="size-10 rounded-xl border border-gray-200 p-0.5 cursor-pointer shadow-sm"
                  />
                  <input
                    type="text"
                    value={labelColor}
                    onChange={(e) => setLabelColor(e.target.value)}
                    className="block w-full rounded-xl border border-gray-200 px-3 py-2 text-xs  uppercase focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Presets */}
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">
                Hazır Renk Temaları
              </label>
              <div className="flex flex-wrap gap-2">
                {PRESET_PALETTES.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    onClick={() => handleApplyPalette(p)}
                    className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 hover:border-gray-300 shadow-sm transition-all"
                  >
                    <span
                      className="size-3.5 rounded-full border border-black/10 inline-block"
                      style={{ backgroundColor: p.bg }}
                    />
                    <span>{p.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="submit"
              disabled={updateSettingsMutation.isPending}
              className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50 shadow-md shadow-blue-200 hover:shadow-lg transition-all"
            >
              {updateSettingsMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Save className="size-4" />
              )}
              <span>Tasarımı Kaydet & Yayınla</span>
            </button>
          </div>
        </form>

        {/* Right Side: Dynamic Apple Wallet Mockup Preview */}
        <div className="lg:col-span-5 flex flex-col items-center justify-start space-y-6">
          <div className="w-full max-w-[340px] rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
              <div className="flex items-center gap-1.5 text-gray-500">
                <Smartphone className="size-4" />
                <span className="text-xs font-bold uppercase tracking-wider">Wallet Önizleme</span>
              </div>
              <span className="flex items-center gap-1 text-[10px] rounded-full bg-emerald-50 text-emerald-700 font-extrabold px-2 py-0.5 border border-emerald-200">
                <Sparkles className="size-2.5" /> DİNAMİK
              </span>
            </div>

            {/* Apple Wallet Visual pass */}
            <div
              className="w-full aspect-[2/3.2] rounded-2xl shadow-xl p-5 flex flex-col justify-between overflow-hidden transition-all duration-300 relative select-none"
              style={{
                backgroundColor: backgroundColor,
                color: foregroundColor,
              }}
            >
              {/* Card top */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex size-7 items-center justify-center rounded-lg bg-white/20 backdrop-blur-md">
                    <Coffee className="size-4" style={{ color: foregroundColor }} />
                  </div>
                  <span className="text-xs font-extrabold uppercase tracking-wide opacity-90">
                    {cardTitle}
                  </span>
                </div>
                <span className="text-[10px] opacity-75 ">VIDYON</span>
              </div>

              {/* Card Body - Stamps Grid */}
              <div className="my-auto space-y-4">
                <div className="text-center">
                  <div
                    className="text-[9px] uppercase tracking-wider font-semibold opacity-75"
                    style={{ color: labelColor }}
                  >
                    Güncel Durum
                  </div>
                  <div className="text-3xl font-black mt-0.5">4 / {targetStamps}</div>
                </div>

                {/* Stamp visual representation */}
                <div className="grid grid-cols-5 gap-2 justify-center">
                  {Array.from({ length: targetStamps }).map((_, i) => {
                    const isStamped = i < 4;
                    return (
                      <div
                        key={i}
                        className={`aspect-square rounded-full border flex items-center justify-center transition-all ${
                          isStamped ? "bg-white/30 border-white" : "bg-black/10 border-white/20"
                        }`}
                        title={isStamped ? "Damgalanmış" : "Boş"}
                      >
                        <Coffee
                          className={`size-3.5 transition-all ${isStamped ? "opacity-100 scale-110" : "opacity-30 scale-90"}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Card Bottom - Barcode */}
              <div className="bg-white rounded-xl p-3 flex flex-col items-center gap-1 shadow-md">
                {/* Simulated Code 128 barcode lines */}
                <div className="w-full h-10 flex items-stretch gap-[1px]">
                  {Array.from({ length: 48 }).map((_, idx) => {
                    const isBar = (idx * 7) % 11 > 3;
                    return (
                      <div
                        key={idx}
                        className="flex-1 bg-black"
                        style={{ opacity: isBar ? 1 : 0 }}
                      />
                    );
                  })}
                </div>
                <span className="text-[9px]  text-black font-extrabold tracking-widest mt-1">
                  CUST-KOD128
                </span>
              </div>

              {/* Info fields */}
              <div className="flex justify-between items-center mt-3 pt-2 border-t border-white/10 text-xs">
                <div>
                  <span
                    className="block text-[8px] uppercase font-semibold opacity-60"
                    style={{ color: labelColor }}
                  >
                    Müşteri
                  </span>
                  <span className="font-bold">Ahmet Yılmaz</span>
                </div>
                <div className="text-right">
                  <span
                    className="block text-[8px] uppercase font-semibold opacity-60"
                    style={{ color: labelColor }}
                  >
                    Ödüller
                  </span>
                  <span className="font-bold">1 Bedava</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Info Alerts */}
          <div className="w-full max-w-[340px] rounded-xl border border-blue-100 bg-blue-50/50 p-4 text-xs text-blue-700 space-y-2">
            <div className="font-bold flex items-center gap-1.5 text-blue-800">
              <CheckCircle2 className="size-3.5 shrink-0 text-blue-600" />
              Tasarım Değişikliği Bildirimleri
            </div>
            <p className="leading-relaxed">
              Tasarımı kaydettiğinizde, Apple ve Google Wallet kullanan tüm müşterilerinizin
              telefonundaki kartlar arka planda **anında güncellenir.**
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
